import { SpeedToLeadSequenceEvent, SpeedToLeadTouchpointEvent } from 'types/recommendations';
import { formatCountWithUnit } from 'utils/helpers/string';

import {
  SPEED_TO_LEAD_HTML_URL_CLEANUP_DELAY_MS,
  SPEED_TO_LEAD_NO_TOUCHES_LABEL,
  SPEED_TO_LEAD_SECONDS_PER_DAY,
  SPEED_TO_LEAD_SECONDS_PER_HOUR,
  SPEED_TO_LEAD_SECONDS_PER_MINUTE,
  SPEED_TO_LEAD_TOUCH_LABEL,
  SPEED_TO_LEAD_TOUCHES_LABEL,
} from './constants';
import { getEventChannel } from './speedToLeadOverallUtils';

export const formatElapsed = (seconds: number) => {
  const safeSeconds = Math.max(0, seconds);
  const days = Math.floor(safeSeconds / SPEED_TO_LEAD_SECONDS_PER_DAY);
  const hours = Math.floor((safeSeconds % SPEED_TO_LEAD_SECONDS_PER_DAY) / SPEED_TO_LEAD_SECONDS_PER_HOUR);
  const minutes = Math.floor((safeSeconds % SPEED_TO_LEAD_SECONDS_PER_HOUR) / SPEED_TO_LEAD_SECONDS_PER_MINUTE);
  const parts = [
    days ? formatCountWithUnit(days, 'day') : '',
    hours ? formatCountWithUnit(hours, 'hour') : '',
    minutes ? formatCountWithUnit(minutes, 'minute') : '',
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' and ') : 'Less than a minute';
};

export const formatTime = (occurredAt: string) =>
  new Date(occurredAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export const formatMediaDuration = (seconds: number) => {
  const roundedSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(roundedSeconds / SPEED_TO_LEAD_SECONDS_PER_HOUR);
  const minutes = Math.floor((roundedSeconds % SPEED_TO_LEAD_SECONDS_PER_HOUR) / SPEED_TO_LEAD_SECONDS_PER_MINUTE);
  const remainingSeconds = roundedSeconds % SPEED_TO_LEAD_SECONDS_PER_MINUTE;
  const minuteSeconds = `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  return hours ? `${String(hours).padStart(2, '0')}:${minuteSeconds}` : minuteSeconds;
};

export const downloadHtml = (event: SpeedToLeadTouchpointEvent) => {
  if (!event.email?.html_body) {
    return;
  }
  const url = URL.createObjectURL(new Blob([event.email.html_body], { type: 'text/html' }));
  const link = document.createElement('a');
  const timestamp = new Date(event.occurred_at)
    .toISOString()
    .replace(/\.\d{3}Z$/, 'Z')
    .replaceAll(':', '-');
  link.href = url;
  link.download = `${timestamp}_${getEventChannel(event)}.html`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), SPEED_TO_LEAD_HTML_URL_CLEANUP_DELAY_MS);
};

export const formatTouchCount = (count: number) =>
  count === 0
    ? SPEED_TO_LEAD_NO_TOUCHES_LABEL
    : formatCountWithUnit(count, SPEED_TO_LEAD_TOUCH_LABEL, SPEED_TO_LEAD_TOUCHES_LABEL);

export const getTouchpointKey = (event: SpeedToLeadSequenceEvent, events: SpeedToLeadSequenceEvent[]) =>
  event.activity_id ?? `${event.occurred_at}-${events.indexOf(event)}`;
